/**
 * Array with at least one element
 *
 * fp-ts has NonEmptyArray type too, but it is slightly differently typed (extension of Array with {0: T})
 * and we preserve our implementation which performs better in some cases, e.g.
 * ```
 * declare function sum(...numbers: NonEmptyArray<number>): number;
 * const numbers: NonEmptyArray<number> = [1, 2, 3];
 * // OK with our NonEmptyArray, error with fp-ts's NonEmptyArray
 * const result = sum(...numbers);
 * ```
 */
export type NonEmptyArray<T> = [T, ...T[]];

/**
 * Concatenate arrays where at least one (must be placed from 1st to 3rd argument for type checking)
 * is non empty, so the result is also non empty.
 */
function concat<T>(a1: NonEmptyArray<T>, ...arr: T[][]): NonEmptyArray<T>;
function concat<T>(
  a1: T[],
  a2: NonEmptyArray<T>,
  ...arr: T[][]
): NonEmptyArray<T>;
function concat<T>(
  a1: T[],
  a2: T[],
  a3: NonEmptyArray<T>,
  ...arr: T[][]
): NonEmptyArray<T>;
function concat<T>(...arr: T[][]): T[] {
  return arr.reduce((result, a) => result.concat(a));
}

const isNonEmpty = <T>(a: T[]): a is NonEmptyArray<T> => a.length > 0;

const map =
  <A, B>(f: (a: A) => B) =>
  (fa: NonEmptyArray<A>): NonEmptyArray<B> =>
    fa.map((a) => f(a)) as NonEmptyArray<B>;

export const nonEmptyArray = {
  concat,
  isNonEmpty,
  map,
};
